"use client";

import { useAuth as clerkUseAuth, useUser as clerkUseUser, useSession as clerkUseSession, useOrganization as clerkUseOrganization, useOrganizationList as clerkUseOrganizationList, Show as ClerkShow } from "@clerk/nextjs";
import React, { useEffect, useState, ReactNode, createElement } from "react";
import { use_lti_mode } from "@/providers/lti-provider";

// Re-export for convenience (components can import from here or directly from use-lti-context)
export { use_lti_mode };

async function fetch__lti_user_data() {
	try {
		const response = await fetch("/api/auth/current");
		return response.ok ? await response.json() : null;
	} catch {
		return null;
	}
}

export function useAuth() {
	const clerk_auth = clerkUseAuth();
	const is_lti = use_lti_mode();
	const [lti_data, set_lti_data] = useState<any>(null);

	useEffect(() => {
		if (is_lti) fetch__lti_user_data().then(set_lti_data);
	}, [is_lti]);

	if (!is_lti) return clerk_auth;

	// In LTI mode, consider user signed in even while data is loading
	return {
		...clerk_auth,
		userId: lti_data?.user?.clerk?.id || null,
		user: lti_data?.user ?? null,
		isSignedIn: true,
		isLoaded: !!lti_data,
		orgId: lti_data?.orgId || null,
		orgRole: lti_data?.orgRole || null,
		getToken: async () => null, // LTI bearer auth is attached by the service worker.
		sessionClaims: lti_data?.sessionClaims || null,
	} as any;
}

export function useUser() {
	const clerk_user = clerkUseUser();
	const is_lti = use_lti_mode();
	const [lti_data, set_lti_data] = useState<any>(null);

	useEffect(() => {
		if (is_lti) fetch__lti_user_data().then(set_lti_data);
	}, [is_lti]);

	if (!is_lti) return clerk_user;

	return {
		...clerk_user,
		user: lti_data?.user?.profile || null,
		isLoaded: !!lti_data,
		isSignedIn: !!lti_data?.user,
	} as any;
}

/*
TODO: for now still only gets session data from clerk, need to look into if there any session lti data that would be relevant here
only needed for running session.reload() in non lti mode atm
*/
export function useSession() {
	const clerk_session = clerkUseSession();
	const is_lti = use_lti_mode();
	const [lti_data, set_lti_data] = useState<any>(null);

	useEffect(() => {
		if (is_lti) fetch__lti_user_data().then(set_lti_data);
	}, [is_lti]);

	if (!is_lti) return clerk_session;

	return {
		...clerk_session,
		ltiUser: lti_data,
		isLoaded: !!lti_data,
		isSignedIn: !!lti_data?.user,
	} as any;
}

export function useOrganization() {
	const clerk_org = clerkUseOrganization();
	const is_lti = use_lti_mode();

	if (!is_lti) return clerk_org;

	return {
		...clerk_org,
		organization: null,
		isLoaded: true,
	} as any;
}

export function useOrganizationList(options?: Parameters<typeof clerkUseOrganizationList>[0]) {
	const clerk_org_list = clerkUseOrganizationList(options);
	const is_lti = use_lti_mode();

	if (!is_lti) return clerk_org_list;

	return {
		...clerk_org_list,
		userMemberships: { data: [], count: 0 },
		isLoaded: true,
		setActive: async () => {},
	} as any;
}

// Protect wrapper component that works with both Clerk and LTI sessions
interface ProtectProps {
	condition?: (has: (params: { role: string }) => boolean) => boolean;
	role?: string;
	children: ReactNode;
	fallback?: ReactNode;
}

export function Protect({ condition, role, children, fallback }: ProtectProps) {
	const { orgRole, isSignedIn, isLoaded } = useAuth();
	const is_lti = use_lti_mode();

	// In Clerk mode, use Clerk's Protect component
	if (!is_lti) {
		if (condition) {
			return createElement(ClerkShow, { when: condition, fallback }, children);
		}
		if (role) {
			return createElement(ClerkShow, { when: { role }, fallback }, children);
		}
		return createElement(React.Fragment, {}, children);
	}

	// In LTI mode, check orgRole directly
	if (!isLoaded) {
		return fallback || null;
	}

	if (!isSignedIn) {
		return fallback || null;
	}

	// Handle role prop
	if (role) {
		// Map role prop to orgRole format
		const role_mapping: Record<string, string> = {
			admin: "org:admin",
			student: "org:student",
			professor: "org:professor",
		};
		const mapped_role = role_mapping[role] || role;
		if (orgRole === mapped_role) {
			return createElement(React.Fragment, {}, children);
		}
		return fallback || null;
	}

	// Handle condition prop
	if (condition) {
		const has = (params: { role: string }) => {
			return orgRole === params.role;
		};
		if (condition(has)) {
			return createElement(React.Fragment, {}, children);
		}
		return fallback || null;
	}

	// No condition or role specified, show children
	return createElement(React.Fragment, {}, children);
}
